(() => {
  if (customElements.get('td-travel-offer-picker')) return;

  class TDTravelOfferPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      this.mainSection = document.getElementById(`MainProduct-${this.sectionId}`) || this.closest('[id^="MainProduct-"]');
      this.productForm = this.form?.closest('product-form');
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.submitButton = this.productForm?.querySelector('[type="submit"]') || this.form?.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.querySelector('span:not(.sold-out-message)');
      this.variantPicker = this.mainSection?.querySelector('variant-radios, variant-selects');
      this.radios = Array.from(this.querySelectorAll('[data-td-travel-radio]'));
      this.cards = Array.from(this.querySelectorAll('[data-td-travel-card]'));
      this.bundleCard = this.querySelector('[data-td-travel-card="bundle"]');
      this.soloGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.bundleGallery = document.getElementById(`MediaGallery-${this.sectionId}-bundle`);
      this.productModal = document.getElementById(`ProductModal-${this.sectionId}`);
      this.productModalTemplate = document.getElementById(`ProductModalTemplate-${this.sectionId}`);
      this.status = this.querySelector('[data-td-travel-status]');
      this.safetyNote = this.querySelector('[data-td-travel-safety]');
      this.sticky = this.querySelector('[data-td-travel-sticky]');
      this.stickyTitle = this.querySelector('[data-td-travel-sticky-title]');
      this.stickyPrice = this.querySelector('[data-td-travel-sticky-price]');
      this.stickyCyberPriceLabel = this.querySelector('[data-td-travel-sticky-cyber-price-label]');
      this.stickyButton = this.querySelector('[data-td-travel-sticky-submit]');
      this.stickyLabel = this.querySelector('[data-td-travel-sticky-label]');
      this.mainData = this.readJson('[data-td-travel-main-json]');
      this.bundleData = this.readJson('[data-td-travel-bundle-json]');
      this.mainVariants = Array.isArray(this.mainData.variants) ? this.mainData.variants : [];
      this.bundleVariants = Array.isArray(this.bundleData.variants) ? this.bundleData.variants : [];
      this.addonPrice = Number(this.dataset.addonPrice || 0);
      this.addonCompareAt = Number(this.dataset.addonCompareAt || 0);
      this.currency = this.dataset.currency || 'CLP';
      this.locale = this.dataset.locale || 'es-CL';
      this.experimentName = this.dataset.experimentName || 'auto_offer_v1';
      this.enableMobileSticky = this.dataset.stickyEnabled === 'true';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Agotado';
      this.selectedOffer = 'solo';
      this.selectedOfferHasDiscount = false;
      this.mainVariant = null;
      this.bundleVariant = null;
      this.targetVariant = null;
      this.submitting = false;
      this.scrollTicking = false;
      this.viewTracked = false;
      this.activeGalleryOffer = null;
      this.galleryMediaState = new WeakMap();
      this.galleryVariantState = { solo: null, bundle: null };

      if (!this.form || !this.variantInput || !this.submitButton || !this.radios.length || !this.mainVariants.length) {
        this.dataset.initializationError = 'true';
        return;
      }

      this.productForm?.classList.add('td-travel-product-form');

      this.onRadioChange = this.onRadioChange.bind(this);
      this.onVariantPickerChange = this.onVariantPickerChange.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this);
      this.onStickyClick = this.onStickyClick.bind(this);
      this.onViewportActivity = this.onViewportActivity.bind(this);
      this.onProductFormStart = this.onProductFormStart.bind(this);
      this.onProductFormSuccess = this.onProductFormSuccess.bind(this);
      this.onProductFormError = this.onProductFormError.bind(this);
      this.onProductFormEnd = this.onProductFormEnd.bind(this);
      this.onModalBuilt = this.onModalBuilt.bind(this);

      this.radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange));
      this.variantPicker?.addEventListener('change', this.onVariantPickerChange);
      this.variantInput.addEventListener('change', this.onVariantInputChange);
      this.form.addEventListener('submit', this.onFormSubmitCapture, true);
      this.stickyButton?.addEventListener('click', this.onStickyClick);
      window.addEventListener('scroll', this.onViewportActivity, { passive: true });
      window.addEventListener('resize', this.onViewportActivity, { passive: true });
      document.addEventListener('td:product-form:submit-start', this.onProductFormStart);
      document.addEventListener('td:product-form:submit-success', this.onProductFormSuccess);
      document.addEventListener('td:product-form:submit-error', this.onProductFormError);
      document.addEventListener('td:product-form:submit-end', this.onProductFormEnd);
      document.addEventListener('td:product-modal-built', this.onModalBuilt);

      this.submitObserver = new MutationObserver(() => {
        if (this.submitting || this.ctaSyncScheduled) return;
        this.ctaSyncScheduled = true;
        window.requestAnimationFrame(() => {
          this.ctaSyncScheduled = false;
          this.updateFormTarget();
          this.updateCta();
          this.updateSticky();
        });
      });
      this.submitObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true
      });

      this.hideSingleVariantPicker();
      this.mainVariant = this.getSelectedMainVariant() || this.findMainVariantById(this.variantInput.value) || this.mainVariants[0];

      const soloRadio = this.radios.find((radio) => radio.dataset.offerKey === 'solo');
      if (soloRadio && !soloRadio.disabled) {
        soloRadio.checked = true;
        this.selectedOffer = 'solo';
      }

      this.refresh({ announce: false, track: false });
      this.trackSelectorView();
      this.updateStickyVisibility();
    }

    disconnectedCallback() {
      this.radios?.forEach((radio) => radio.removeEventListener('change', this.onRadioChange));
      this.variantPicker?.removeEventListener('change', this.onVariantPickerChange);
      this.variantInput?.removeEventListener('change', this.onVariantInputChange);
      this.form?.removeEventListener('submit', this.onFormSubmitCapture, true);
      this.stickyButton?.removeEventListener('click', this.onStickyClick);
      window.removeEventListener('scroll', this.onViewportActivity);
      window.removeEventListener('resize', this.onViewportActivity);
      document.removeEventListener('td:product-form:submit-start', this.onProductFormStart);
      document.removeEventListener('td:product-form:submit-success', this.onProductFormSuccess);
      document.removeEventListener('td:product-form:submit-error', this.onProductFormError);
      document.removeEventListener('td:product-form:submit-end', this.onProductFormEnd);
      document.removeEventListener('td:product-modal-built', this.onModalBuilt);
      this.viewObserver?.disconnect();
      this.submitObserver?.disconnect();
      this.productForm?.classList.remove('td-travel-product-form');
      document.documentElement.classList.remove('td-travel-sticky-visible');
    }

    readJson(selector) {
      const element = this.querySelector(selector);
      if (!element) return {};

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('[TD Travel Offer] JSON inválido', error);
        return {};
      }
    }

    hideSingleVariantPicker() {
      if (this.mainVariants.length !== 1 || !this.variantPicker) return;
      this.variantPicker.hidden = true;
      this.variantPicker.setAttribute('aria-hidden', 'true');
      this.variantPicker.dataset.tdTravelHidden = 'true';
    }

    findMainVariantById(id) {
      return this.mainVariants.find((variant) => String(variant.id) === String(id));
    }

    getSelectedMainVariant() {
      if (!this.variantPicker) return null;

      let values = [];
      if (this.variantPicker.matches('variant-radios')) {
        values = Array.from(this.variantPicker.querySelectorAll('fieldset')).map((fieldset) => {
          return fieldset.querySelector('input:checked')?.value;
        });
      } else {
        values = Array.from(this.variantPicker.querySelectorAll('select')).map((select) => select.value);
      }

      if (!values.length || values.some((value) => typeof value === 'undefined')) return null;

      return this.mainVariants.find((variant) => {
        return Array.isArray(variant.options) && variant.options.every((option, index) => String(option) === String(values[index]));
      }) || null;
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    getMatchingBundleVariant(mainVariant) {
      if (!mainVariant || !this.bundleVariants.length) return null;
      if (this.bundleVariants.length === 1) return this.bundleVariants[0];

      const normalizedTitle = this.normalize(mainVariant.title);
      const exactTitle = this.bundleVariants.find((variant) => this.normalize(variant.title) === normalizedTitle);
      if (exactTitle) return exactTitle;

      const mainOptionNames = Array.isArray(this.mainData.options) ? this.mainData.options : [];
      const bundleOptionNames = Array.isArray(this.bundleData.options) ? this.bundleData.options : [];
      const sharedOptions = [];

      mainOptionNames.forEach((name, mainIndex) => {
        const bundleIndex = bundleOptionNames.findIndex((bundleName) => this.normalize(bundleName) === this.normalize(name));
        if (bundleIndex >= 0) sharedOptions.push({ mainIndex, bundleIndex });
      });

      if (!sharedOptions.length) return null;

      return this.bundleVariants.find((variant) => {
        return sharedOptions.every(({ mainIndex, bundleIndex }) => {
          return this.normalize(variant.options?.[bundleIndex]) === this.normalize(mainVariant.options?.[mainIndex]);
        });
      }) || null;
    }

    onRadioChange(event) {
      const radio = event.currentTarget;
      if (!radio.checked || radio.disabled) return;

      this.selectedOffer = radio.dataset.offerKey || 'solo';
      if (this.selectedOffer === 'bundle') this.preloadBundleHero();
      this.refresh({ announce: true, track: true });
    }

    onVariantPickerChange() {
      window.setTimeout(() => {
        const selected = this.getSelectedMainVariant();
        if (selected) this.mainVariant = selected;
        this.refresh({ announce: false, track: false });
      }, 0);
    }

    onVariantInputChange() {
      const mainVariant = this.findMainVariantById(this.variantInput.value);
      if (!mainVariant) return;

      this.mainVariant = mainVariant;
      window.setTimeout(() => this.refresh({ announce: false, track: false }), 0);
    }

    refresh({ announce = false, track = false } = {}) {
      if (!this.mainVariant) return;

      this.bundleVariant = this.getMatchingBundleVariant(this.mainVariant);
      this.updateAvailability();

      if (this.selectedOffer === 'bundle' && !this.isBundleAvailable()) {
        const soloRadio = this.radios.find((radio) => radio.dataset.offerKey === 'solo' && !radio.disabled);
        if (soloRadio) {
          soloRadio.checked = true;
          this.selectedOffer = 'solo';
          if (announce && this.status) {
            this.status.textContent = 'El pack no está disponible para esta variante. Mantuvimos la opción Solo.';
          }
        }
      }

      this.targetVariant = this.selectedOffer === 'bundle' ? this.bundleVariant : this.mainVariant;
      this.updateCards();
      this.switchGallery(this.selectedOffer);
      this.syncModalMedia(this.selectedOffer);
      this.updatePrices();
      this.updateFormTarget();
      this.updateCta();
      this.updateSafetyNote();
      this.updateSticky();
      this.updateStickyVisibility();

      const selectedRadio = this.radios.find((radio) => radio.dataset.offerKey === this.selectedOffer);
      const detail = this.getAnalyticsDetail();

      if (announce && this.status && this.targetVariant) {
        const optionTitle = selectedRadio?.closest('[data-td-travel-card]')?.querySelector('.td-travel-card__title')?.textContent?.trim() || '';
        this.status.textContent = `Opción seleccionada: ${optionTitle}, ${this.formatMoney(this.targetVariant.price)}.`;
      }

      document.dispatchEvent(new CustomEvent('td:travel-offer:change', { bubbles: true, detail }));
      if (track) this.pushDataLayer('offer_option_clicked', detail);
    }

    isSoloAvailable() {
      return Boolean(this.mainVariant?.available);
    }

    isBundleAvailable() {
      return Boolean(this.mainVariant?.available && this.bundleVariant?.available);
    }

    updateAvailability() {
      this.radios.forEach((radio) => {
        const key = radio.dataset.offerKey;
        const available = key === 'bundle' ? this.isBundleAvailable() : this.isSoloAvailable();
        const card = radio.closest('[data-td-travel-card]');
        const unavailable = card?.querySelector('[data-td-travel-unavailable]');

        radio.disabled = !available;
        card?.classList.toggle('is-unavailable', !available);
        if (unavailable) {
          unavailable.hidden = available;
          unavailable.textContent = key === 'bundle' ? 'Temporalmente no disponible' : this.soldOutLabel;
        }
      });
    }

    updateCards() {
      this.cards.forEach((card) => {
        const selected = card.dataset.tdTravelCard === this.selectedOffer;
        card.classList.toggle('is-selected', selected);
        card.toggleAttribute('data-selected', selected);
      });
    }

    switchGallery(offerKey) {
      const requestedOffer = offerKey === 'bundle' && this.bundleGallery ? 'bundle' : 'solo';
      const requestedVariantId = requestedOffer === 'bundle' ? this.bundleVariant?.id : this.mainVariant?.id;
      const variantChanged = String(this.galleryVariantState[requestedOffer] || '') !== String(requestedVariantId || '');

      if (this.activeGalleryOffer === requestedOffer) {
        if (variantChanged) {
          const currentGallery = requestedOffer === 'bundle' ? this.bundleGallery : this.soloGallery;
          if (currentGallery) {
            window.requestAnimationFrame(() => this.refreshGallery(currentGallery, requestedOffer, true));
          }
        }
        return;
      }

      const currentGallery = this.activeGalleryOffer === 'bundle' ? this.bundleGallery : this.soloGallery;
      if (currentGallery) this.rememberGalleryMedia(currentGallery);

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      [
        { key: 'solo', element: this.soloGallery },
        { key: 'bundle', element: this.bundleGallery }
      ].forEach(({ key, element }) => {
        if (!element) return;
        const active = key === requestedOffer;
        element.hidden = !active;
        element.setAttribute('aria-hidden', String(!active));
        element.toggleAttribute('inert', !active);
      });

      this.activeGalleryOffer = requestedOffer;
      const activeGallery = requestedOffer === 'bundle' ? this.bundleGallery : this.soloGallery;
      if (!activeGallery) return;

      window.requestAnimationFrame(() => this.refreshGallery(activeGallery, requestedOffer, variantChanged));
    }

    rememberGalleryMedia(gallery) {
      const activeMedia = gallery.querySelector('.product__media-item.is-active[data-media-id]:not([hidden])');
      if (activeMedia?.dataset.mediaId) {
        this.galleryMediaState.set(gallery, activeMedia.dataset.mediaId);
      }
    }

    getVisibleGalleryMedia(gallery) {
      return Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]')).filter((item) => {
        return !item.hidden && item.style.display !== 'none';
      });
    }

    getVariantGalleryMediaId(offerKey) {
      const isBundle = offerKey === 'bundle';
      const variant = isBundle ? this.bundleVariant : this.mainVariant;
      const productData = isBundle ? this.bundleData : this.mainData;
      const mediaId = productData.variantMedia?.[String(variant?.id || '')];

      if (!mediaId) return null;
      return isBundle ? `${this.sectionId}-bundle-${mediaId}` : `${this.sectionId}-${mediaId}`;
    }

    refreshGallery(gallery, offerKey, variantChanged = false) {
      const visibleMedia = this.getVisibleGalleryMedia(gallery);
      if (!visibleMedia.length) return;

      const variantMediaId = variantChanged ? this.getVariantGalleryMediaId(offerKey) : null;
      const variantMedia = variantMediaId
        ? visibleMedia.find((item) => item.dataset.mediaId === variantMediaId)
        : null;
      const rememberedId = this.galleryMediaState.get(gallery);
      const rememberedMedia = rememberedId
        ? visibleMedia.find((item) => item.dataset.mediaId === rememberedId)
        : null;
      const currentMedia = visibleMedia.find((item) => item.classList.contains('is-active'));
      const targetMedia = variantMedia || rememberedMedia || currentMedia || visibleMedia[0];

      if (typeof gallery.setActiveMedia === 'function' && !targetMedia.classList.contains('is-active')) {
        gallery.setActiveMedia(targetMedia.dataset.mediaId, false);
      } else {
        visibleMedia.forEach((item) => item.classList.toggle('is-active', item === targetMedia));
      }

      this.galleryMediaState.set(gallery, targetMedia.dataset.mediaId);
      this.galleryVariantState[offerKey] = offerKey === 'bundle' ? this.bundleVariant?.id || null : this.mainVariant?.id || null;

      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
      const thumbnails = gallery.querySelector('slider-component[id^="GalleryThumbnails"]');

      if (typeof viewer?.resetPages === 'function') viewer.resetPages();
      if (typeof thumbnails?.resetPages === 'function') thumbnails.resetPages();
    }

    syncModalMedia(offerKey) {
      const activeOffer = offerKey === 'bundle' && this.bundleGallery ? 'bundle' : 'solo';
      const syncChildren = (container) => {
        if (!container) return;

        Array.from(container.children).forEach((item) => {
          const mediaOffer = item.dataset.tdOfferMedia;
          if (!mediaOffer) return;
          item.hidden = mediaOffer !== activeOffer;
          item.classList.remove('active');
        });
      };

      if (this.productModalTemplate?.content) syncChildren(this.productModalTemplate.content);
      syncChildren(this.productModal?.querySelector('.product-media-modal__content'));

      if (typeof this.productModal?.refreshMedia === 'function') {
        this.productModal.refreshMedia();
      }
    }

    onModalBuilt(event) {
      if (String(event.detail?.sectionId) !== String(this.sectionId)) return;
      this.productModalTemplate = null;
      this.syncModalMedia(this.selectedOffer);
    }

    preloadBundleHero() {
      if (!this.bundleGallery || this.bundleGallery.dataset.tdHeroPreloaded === 'true') return;

      const firstImage = this.bundleGallery.querySelector('.product__media-item img');
      if (!firstImage) return;

      this.bundleGallery.dataset.tdHeroPreloaded = 'true';
      firstImage.loading = 'eager';
      firstImage.fetchPriority = 'low';
    }

    updatePrices() {
      const soloPrice = Number(this.mainVariant.price || 0);
      const soloCompare = Number(this.mainVariant.compare_at_price || 0);
      const soloRegular = soloCompare > soloPrice ? soloCompare : soloPrice;
      const soloHasDiscount = this.mainVariant.available && soloCompare > soloPrice;

      this.selectedOfferHasDiscount = this.selectedOffer === 'solo' && soloHasDiscount;

      this.setMoney('[data-td-solo-price]', soloPrice);
      this.setMoney('[data-td-solo-compare]', soloRegular);
      this.toggle('[data-td-solo-reference]', soloHasDiscount);
      this.toggle('[data-td-cyber-public-price-label="solo"]', soloHasDiscount);

      if (!this.bundleVariant) {
        if (this.selectedOffer === 'bundle') this.selectedOfferHasDiscount = false;
        this.toggle('[data-td-bundle-reference]', false);
        this.toggle('[data-td-cyber-public-price-label="bundle"]', false);
        return;
      }

      const bundlePrice = Number(this.bundleVariant.price || 0);
      const addonRegular = this.addonCompareAt > this.addonPrice ? this.addonCompareAt : this.addonPrice;
      const calculatedRegular = soloRegular + addonRegular;
      const bundleCompare = Number(this.bundleVariant.compare_at_price || 0);
      const bundleRegular = Math.max(calculatedRegular, bundleCompare, bundlePrice);
      const bundleHasDiscount = this.isBundleAvailable() && bundleRegular > bundlePrice;

      if (this.selectedOffer === 'bundle') this.selectedOfferHasDiscount = bundleHasDiscount;

      this.setMoney('[data-td-bundle-price]', bundlePrice);
      this.setMoney('[data-td-bundle-compare]', bundleRegular);
      this.toggle('[data-td-bundle-reference]', bundleHasDiscount);
      this.toggle('[data-td-cyber-public-price-label="bundle"]', bundleHasDiscount);
    }

    setMoney(selector, cents, suffix = '') {
      const element = this.querySelector(selector);
      if (element) element.textContent = `${this.formatMoney(cents)}${suffix}`;
    }

    toggle(selector, visible) {
      const element = this.querySelector(selector);
      if (element) element.hidden = !visible;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      if (this.currency.toUpperCase() === 'CLP') {
        return `$${Math.round(amount).toLocaleString('es-CL')}`;
      }

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(amount).replace(/\s+/g, '');
      } catch (error) {
        return `$${Math.round(amount).toLocaleString('es-CL')}`;
      }
    }

    updateFormTarget() {
      const available = this.selectedOffer === 'bundle' ? this.isBundleAvailable() : this.isSoloAvailable();
      const targetId = this.targetVariant?.id;

      this.form.dataset.tdTravelOffer = this.selectedOffer;
      this.form.dataset.tdTravelMainVariantId = String(this.mainVariant?.id || '');
      this.form.dataset.tdTravelTargetVariantId = String(targetId || '');

      if (targetId) this.variantInput.value = targetId;

      if (this.submitButton.disabled !== !available) this.submitButton.disabled = !available;
      const ariaDisabled = String(!available || this.submitting);
      if (this.submitButton.getAttribute('aria-disabled') !== ariaDisabled) {
        this.submitButton.setAttribute('aria-disabled', ariaDisabled);
      }
    }

    getSelectedCardTitle() {
      return this.cards.find((card) => card.dataset.tdTravelCard === this.selectedOffer)
        ?.querySelector('.td-travel-card__title')?.textContent?.trim() || '';
    }

    getSelectedCtaLabel() {
      return this.radios.find((radio) => radio.dataset.offerKey === this.selectedOffer)?.dataset.ctaLabel || 'Agregar al carrito';
    }

    updateCta() {
      const available = this.selectedOffer === 'bundle' ? this.isBundleAvailable() : this.isSoloAvailable();
      const price = this.targetVariant ? this.formatMoney(this.targetVariant.price) : '';
      const label = this.getSelectedCtaLabel();

      if (this.submitLabel) {
        const buttonText = available && price ? `${label} — ${price}` : this.soldOutLabel;
        if (this.submitLabel.textContent !== buttonText) this.submitLabel.textContent = buttonText;
        this.submitLabel.classList.remove('hidden');
      }

      const ariaLabel = available && price ? `${label}: ${this.getSelectedCardTitle()} por ${price}` : this.soldOutLabel;
      if (this.submitButton.getAttribute('aria-label') !== ariaLabel) this.submitButton.setAttribute('aria-label', ariaLabel);
    }

    updateSafetyNote() {
      if (this.safetyNote) this.safetyNote.hidden = this.selectedOffer !== 'bundle';
    }

    updateSticky() {
      const available = this.selectedOffer === 'bundle' ? this.isBundleAvailable() : this.isSoloAvailable();
      const price = this.targetVariant ? this.formatMoney(this.targetVariant.price) : '';
      const label = this.getSelectedCtaLabel();

      if (this.stickyTitle) this.stickyTitle.textContent = this.getSelectedCardTitle();
      if (this.stickyPrice) this.stickyPrice.textContent = price;
      if (this.stickyCyberPriceLabel) {
        this.stickyCyberPriceLabel.hidden = !(available && this.targetVariant?.available && this.selectedOfferHasDiscount);
      }
      if (this.stickyLabel) this.stickyLabel.textContent = label;
      if (this.stickyButton) {
        this.stickyButton.disabled = !available || this.submitting;
        this.stickyButton.setAttribute('aria-disabled', String(!available || this.submitting));
        this.stickyButton.setAttribute('aria-label', available ? `${label}: ${this.getSelectedCardTitle()} por ${price}` : this.soldOutLabel);
      }
    }

    onFormSubmitCapture(event) {
      const available = this.selectedOffer === 'bundle' ? this.isBundleAvailable() : this.isSoloAvailable();
      if (!available || !this.targetVariant?.id) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.status) this.status.textContent = 'Esta opción no está disponible. Elige otra alternativa.';
        return;
      }

      this.variantInput.disabled = false;
      this.variantInput.value = this.targetVariant.id;
    }

    onStickyClick() {
      if (this.submitting || this.stickyButton?.disabled) return;

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.submitButton);
      } else {
        this.submitButton.click();
      }
    }

    onViewportActivity() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateStickyVisibility();
        this.scrollTicking = false;
      });
    }

    updateStickyVisibility() {
      if (!this.sticky) return;

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const designMode = Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
      const buttonRect = this.submitButton?.getBoundingClientRect();
      const passedButton = Boolean(buttonRect && buttonRect.bottom < 0);
      const overlayOpen = Boolean(document.querySelector('cart-drawer.active, cart-drawer.animate, .menu-drawer-container[open], .modal[open], .quick-add-modal[open]'));
      const shouldShow = this.enableMobileSticky && mobile && !designMode && !overlayOpen && passedButton && Boolean(this.targetVariant);

      this.sticky.classList.toggle('is-visible', shouldShow);
      this.sticky.setAttribute('aria-hidden', String(!shouldShow));
      this.sticky.toggleAttribute('inert', !shouldShow);
      if (this.stickyButton) this.stickyButton.tabIndex = shouldShow ? 0 : -1;
      document.documentElement.classList.toggle('td-travel-sticky-visible', shouldShow);
    }

    onProductFormStart(event) {
      if (event.detail?.formId !== this.form.id) return;
      this.submitting = true;
      this.sticky?.classList.add('is-loading');
      this.updateSticky();
      this.pushDataLayer('offer_add_to_cart_attempt', this.getAnalyticsDetail());
    }

    onProductFormSuccess(event) {
      if (event.detail?.formId !== this.form.id) return;
      this.pushDataLayer('offer_add_to_cart_success', this.getAnalyticsDetail());
    }

    onProductFormError(event) {
      if (event.detail?.formId !== this.form.id) return;
      if (this.status) this.status.textContent = event.detail?.message || 'No pudimos agregar esta opción. Intenta nuevamente.';
      this.pushDataLayer('offer_add_to_cart_error', this.getAnalyticsDetail());
    }

    onProductFormEnd(event) {
      if (event.detail?.formId !== this.form.id) return;
      this.submitting = false;
      this.sticky?.classList.remove('is-loading');
      this.updateFormTarget();
      this.updateCta();
      this.updateSticky();
      this.updateStickyVisibility();
    }

    getAnalyticsDetail() {
      return {
        experiment_name: this.experimentName,
        section_id: this.sectionId,
        offer_key: this.selectedOffer,
        main_product_id: Number(this.dataset.mainProductId || 0),
        main_variant_id: Number(this.mainVariant?.id || 0),
        target_product_id: Number(this.selectedOffer === 'bundle' ? this.bundleData.id || 0 : this.mainData.id || 0),
        target_variant_id: Number(this.targetVariant?.id || 0),
        displayed_price: Number(this.targetVariant?.price || 0) / 100,
        currency: this.currency
      };
    }

    pushDataLayer(eventName, detail = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...detail });
    }

    trackSelectorView() {
      const track = () => {
        if (this.viewTracked) return;
        this.viewTracked = true;
        this.preloadBundleHero();
        this.pushDataLayer('offer_selector_view', this.getAnalyticsDetail());
      };

      if (typeof window.IntersectionObserver !== 'function') {
        track();
        return;
      }

      this.viewObserver = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          track();
          this.viewObserver.disconnect();
        }
      }, { threshold: [0.5] });

      this.viewObserver.observe(this);
    }
  }

  customElements.define('td-travel-offer-picker', TDTravelOfferPicker);
})();